import React, { useEffect, useMemo, useState } from 'react';
import DraggableWindow from './DraggableWindow';
import CustomSelect from '../CustomSelect';
import { inventarioService, lojasService } from '../../services/api';

function parseDetectedLines(value) {
  const lines = String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return lines
    .map((line) => {
      const [descricaoRaw, qtdRaw] = line.split(';');
      const descricao = String(descricaoRaw || '').trim();
      const qtd = Number(String(qtdRaw || '1').replace(',', '.'));
      if (!descricao) return null;
      return {
        descricao,
        qtd: Number.isFinite(qtd) && qtd > 0 ? qtd : 1
      };
    })
    .filter(Boolean);
}

const WindowInventarioFoto = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [lojas, setLojas] = useState([]);
  const [filial, setFilial] = useState(String(userData?.loja_id || userData?.cdfil || userData?.CDFIL || ''));
  const [arquivo, setArquivo] = useState(null);
  const [detectedLines, setDetectedLines] = useState('');
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState('');

  const itensDetectados = useMemo(() => parseDetectedLines(detectedLines), [detectedLines]);

  useEffect(() => {
    const carregarLojas = async () => {
      try {
        const response = await lojasService.getLojas();
        if (Array.isArray(response)) {
          const ordenadas = [...response].sort((a, b) => Number(a.numero) - Number(b.numero));
          setLojas(ordenadas);
          if (!filial && ordenadas.length > 0) {
            setFilial(String(ordenadas[0].numero));
          }
        }
      } catch (e) {
        console.error('Erro ao carregar lojas para inventário:', e);
      }
    };

    carregarLojas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = useMemo(() => {
    return !loading && String(filial || '').trim() && (Boolean(arquivo) || itensDetectados.length > 0);
  }, [loading, filial, arquivo, itensDetectados.length]);

  const handleProcessar = async (e) => {
    e.preventDefault();
    setErro('');
    setItens([]);

    if (!String(filial || '').trim()) {
      setErro('Selecione uma filial.');
      return;
    }

    setLoading(true);
    try {
      const data = await inventarioService.processarFoto({
        filial,
        imageFile: arquivo,
        itens: itensDetectados
      });
      setItens(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao processar inventário por foto:', err);
      setErro(err?.message || 'Erro ao processar inventário.');
    } finally {
      setLoading(false);
    }
  };

  const atualizarLinha = (index, field, value) => {
    setItens((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (field === 'qtd') {
        const qtd = Number(String(value || '').replace(',', '.'));
        return { ...row, qtd: Number.isFinite(qtd) && qtd >= 0 ? qtd : row.qtd };
      }
      return { ...row, [field]: value };
    }));
  };

  const handleExportar = async () => {
    if (!itens.length || exportando) return;

    const itemsPayload = itens.map((item) => ({
      codint: item.codint,
      nome: item.nome,
      qtd: Number(item.qtd || 0)
    }));

    setExportando(true);
    setErro('');
    try {
      const blob = await inventarioService.exportarExcel(itemsPayload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventario-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao exportar inventário:', err);
      setErro('Não foi possível exportar o Excel.');
    } finally {
      setExportando(false);
    }
  };

  return (
    <DraggableWindow
      title="Inventário por Foto"
      icon="fa-camera"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={980}
      initialTop={90}
      initialLeft={180}
      maxHeight="90vh"
    >
      <form onSubmit={handleProcessar} className="consulta-form">
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 10, marginBottom: 10 }}>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 12, marginBottom: 4 }}>Filial</label>
            <CustomSelect
              options={lojas.map((loja) => ({ value: loja.numero, label: `${loja.numero} - ${loja.nome}` }))}
              value={filial}
              onChange={setFilial}
              placeholder="Selecione a loja..."
              searchPlaceholder="Buscar loja..."
            />
          </div>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 12, marginBottom: 4 }}>Foto</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              style={{ padding: 8 }}
            />
          </div>
        </div>

        <div className="consulta-form-group" style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, marginBottom: 4 }}>Itens detectados (MVP) - um por linha: descricao;qtd</label>
          <textarea
            value={detectedLines}
            onChange={(e) => setDetectedLines(e.target.value)}
            placeholder={'Exemplo:\nARROZ TIPO 1 5KG;2\nFEIJAO CARIOCA 1KG;1'}
            rows={5}
            style={{ width: '100%', border: '2px solid #d1d5db', borderRadius: 8, padding: 10, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="submit" className="consulta-btn-submit" disabled={!canSubmit}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-magic'}`}></i>
            {loading ? 'Processando...' : 'Processar Foto'}
          </button>
          <button type="button" className="consulta-btn-submit" onClick={handleExportar} disabled={!itens.length || exportando}>
            <i className={`fas ${exportando ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}></i>
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {itens.length ? `${itens.length} item(ns) mapeados` : 'Sem itens mapeados ainda'}
          </span>
        </div>
      </form>

      {erro && (
        <div className="consulta-error" style={{ marginTop: 10 }}>
          <strong><i className="fas fa-exclamation-triangle"></i> {erro}</strong>
        </div>
      )}

      {itens.length > 0 && (
        <div className="historico-tabela" style={{ marginTop: 12, maxHeight: '44vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 120 }}>CODINT</th>
                <th>NOME</th>
                <th style={{ minWidth: 100 }}>QTD</th>
                <th style={{ minWidth: 110 }}>Confiança</th>
                <th>Detectado</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((row, index) => (
                <tr key={`${row.codint || 'sem-cod'}-${index}`}>
                  <td>
                    <input
                      type="text"
                      value={row.codint || ''}
                      onChange={(e) => atualizarLinha(index, 'codint', e.target.value)}
                      style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.nome || ''}
                      onChange={(e) => atualizarLinha(index, 'nome', e.target.value)}
                      style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={row.qtd ?? 0}
                      onChange={(e) => atualizarLinha(index, 'qtd', e.target.value)}
                      style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6 }}
                    />
                  </td>
                  <td>{Number(row.confianca || 0).toFixed(3)}</td>
                  <td>{row.termoDetectado || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DraggableWindow>
  );
};

export default WindowInventarioFoto;
